import { useEffect, useRef, useCallback, ComponentType } from 'react';
import ReactDOM from 'react-dom';
import { useIntl, IntlProvider, IntlShape } from 'react-intl';
import PaddingBox, { Side } from '@uds/padding-box';
import { Container } from '@uds/grid';
import { useBrandId } from '@ux/whitelabel';
import errorBoundary from 'presentation/hocs/errorBoundary';
import visibilitySet from 'presentation/hocs/visibilitySet';
import { RibbonProps, Type } from 'domain/constants/ribbons';
import { usePrivacyPolicyUrl } from 'domain/selectors/common';
import PrivacyPolicyDescriptionMessage from 'presentation/components/privacy/PrivacyPolicyDescriptionMessage';
import PrivacyModal from 'presentation/assets/privacy-manager/v1.0/privacy-modal.min';

import Ribbon from '../Ribbon';

const HOSTEUROPE_BRAND_ID = 'b9c8f0f0-60dd-4cab-9da8-512b352d9c1a';

declare global {
  interface Window {
    ENVIRONMENT: string;
  }
}

const INLINE_CONTAINER_CLASS = 'inline-privacy-container';

const PureInlinePrivacy = ({ id, name, layout, colorway, loading }: RibbonProps) => {
  const intl = useIntl();
  const brandId = useBrandId();
  const privacyPolicyUrl = usePrivacyPolicyUrl();
  const isHostEurope = brandId === HOSTEUROPE_BRAND_ID;
  const managerRef = useRef<{ destroy: () => void } | null>(null);
  const descriptionContainerRef = useRef<HTMLElement | null>(null);

  const initPrivacyModal = useCallback(() => {
    const modalTitle = intl.formatMessage({ id: 'cookie.modal.title' });
    const acceptButtonText = intl.formatMessage({ id: 'cookie.accept' });
    const supportToggleTitle = intl.formatMessage({ id: 'cookie.toggle.support.title' });
    const supportToggleDescription = intl.formatMessage({ id: 'cookie.toggle.support.text' });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const renderDescription = (_container: HTMLElement): HTMLElement => {
      const wrapper = document.createElement('div');
      descriptionContainerRef.current = wrapper;
      ReactDOM.render(
        <IntlProvider
          locale={intl.locale}
          messages={(intl as IntlShape & { messages: Record<string, string> }).messages}
        >
          <PrivacyPolicyDescriptionMessage messageId="cookie.inline.description" privacyPolicyUrl={privacyPolicyUrl} />
        </IntlProvider>,
        wrapper,
      );
      return wrapper;
    };

    managerRef.current = PrivacyModal.init({
      inline: true,
      inlineContainer: INLINE_CONTAINER_CLASS,
      theme: 'hosteurope',
      modalTitle,
      renderDescription,
      acceptButtonText,
      updateButtonText: acceptButtonText,
      supportToggle: {
        title: supportToggleTitle,
        description: supportToggleDescription,
        defaultChecked: false,
      },
      tags: isHostEurope
        ? {
            liveEngage: {
              siteId: '15393996',
              env: window.ENVIRONMENT || 'dev',
            },
          }
        : undefined,
    });
  }, [intl, privacyPolicyUrl, isHostEurope]);

  useEffect(() => {
    initPrivacyModal();

    return () => {
      if (descriptionContainerRef.current) {
        ReactDOM.unmountComponentAtNode(descriptionContainerRef.current);
        descriptionContainerRef.current = null;
      }
      if (managerRef.current) {
        managerRef.current.destroy();
        managerRef.current = null;
      }
    };
  }, [initPrivacyModal]);

  return (
    <Ribbon id={id} name={name} layout={layout} colorway={colorway} loading={loading} type={Type.INLINE_PRIVACY}>
      <Container>
        <PaddingBox side={Side.X}>
          <div className={INLINE_CONTAINER_CLASS} />
        </PaddingBox>
      </Container>
    </Ribbon>
  );
};

export default errorBoundary(visibilitySet(PureInlinePrivacy)) as ComponentType<RibbonProps>;
